import { useEffect, useState } from "react";
import { HiExclamation } from "react-icons/hi";
import { apiClient } from "../auth/apiClient.ts";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { useTranslation } from "../hooks/useTranslation.ts";
import {
  daysUntilExpiry,
  isSubscriptionExpiryUrgent,
  isSubscriptionExpiryVisible,
  subscriptionExpiryTone,
} from "../utils/subscriptionExpiry.ts";

/** Only permission the subscription endpoint is queried with today. */
export const DEFAULT_SUBSCRIPTION_PERMISSION = "Factor_investing_basic";

export interface SubscriptionExpiryBadgeProps {
  /** Product permission to look up the active subscription for. */
  permission?: string;
  /** When provided the badge renders as a button and calls this on click. */
  onClick?: () => void;
}

interface ExpiryState {
  daysLeft: number;
  /** Localised expiration date for the tooltip. */
  formattedDate: string;
}

/**
 * Toolbar badge counting down the days left on the logged-in user's
 * subscription. Only appears inside the final 30 days. Renders nothing when the
 * user is logged out, has no active subscription for the permission, has a
 * subscription set to auto-renew (`will_renew` !== false), is a dev user with
 * no expiry date, or the lookup fails — it is purely informational and
 * never blocks the toolbar.
 */
const SubscriptionExpiryBadge = ({
  permission = DEFAULT_SUBSCRIPTION_PERMISSION,
  onClick,
}: SubscriptionExpiryBadgeProps) => {
  const { user } = useAuthentication();
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<ExpiryState | null>(null);

  useEffect(() => {
    if (!user) {
      setState(null);
      return;
    }

    let cancelled = false;

    apiClient
      .getActiveSubscription(permission)
      .then(({ data }) => {
        if (cancelled) return;
        const subscription = data.results[0];
        // No subscription, or a dev user with no expiry date -> stay hidden.
        if (!subscription || !subscription.expiration) return;

        // The countdown only matters when the subscription will NOT auto-renew.
        // An auto-renewing plan has nothing to warn the user about.
        if (subscription.will_renew !== false) return;

        const days = daysUntilExpiry(subscription.expiration);
        if (days === null) return;

        setState({
          daysLeft: days,
          formattedDate: new Date(subscription.expiration).toLocaleDateString(
            i18n.language,
            { dateStyle: "medium" }
          ),
        });
      })
      .catch(() => {
        // Silent: the badge simply stays hidden.
      });

    return () => {
      cancelled = true;
    };
  }, [user, permission, i18n.language]);

  // Hidden until the subscription is inside its final 30 days.
  if (!state || !isSubscriptionExpiryVisible(state.daysLeft)) return null;

  const { daysLeft, formattedDate } = state;
  const expired = daysLeft <= 0;
  const showWarningIcon = isSubscriptionExpiryUrgent(daysLeft);

  const className = `hidden items-center gap-1 rounded-full px-3 py-1 text-xs font-medium sm:inline-flex ${subscriptionExpiryTone(
    daysLeft
  )}`;
  const title = t("navbar.subscriptionExpiryTitle", { date: formattedDate });
  const content = (
    <>
      {showWarningIcon && <HiExclamation size={14} className="shrink-0" />}
      {expired
        ? t("navbar.subscriptionExpired")
        : t("navbar.subscriptionDaysLeft", { count: daysLeft })}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${className} cursor-pointer transition-opacity hover:opacity-80`}
        title={title}
      >
        {content}
      </button>
    );
  }

  return (
    <span className={className} title={title}>
      {content}
    </span>
  );
};

export default SubscriptionExpiryBadge;
